"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { acquireLenis, releaseLenis } from "@/lib/motion/lenis-engine";
import { TB_SCROLL_TO, easeOutCubic } from "@/lib/motion/motion-tokens";
import { EDITORIAL_REVEAL, useReveals } from "@/lib/motion/use-reveals";

// ─── Configuration (values from the Tech Spec, § Movimento) ──────────────────

/** Lenis smooth-scroll tuning. */
const LENIS_OPTIONS = {
  lerp: 0.08,
  duration: 1.5,
  smoothWheel: true,
  wheelMultiplier: 1.2,
  // The reference drives touch through Lenis too, so a phone gets the same inertia.
  syncTouch: true,
} as const;

/**
 * Anchor navigation (the reference's `navScroll`): lands the section 88px down, clear of the
 * fixed header, over 1.2s on an out-cubic curve — the product's one smooth scroll.
 */
const SECTION_SCROLL_OFFSET_PX = TB_SCROLL_TO.offsetPx;
const SECTION_SCROLL_DURATION_S = TB_SCROLL_TO.durationS;

/** Hero displacement, as a fraction of the scroll offset. */
const HERO_PARALLAX_FACTOR = 0.18;

/** Newsletter displacement, as a fraction of its distance from the viewport centre. */
const CIRCLE_PARALLAX_FACTOR = 0.08;

/** Portal photo displacement, as a fraction of its frame's distance from the viewport centre. */
const BANNER_PARALLAX_FACTOR = 0.08;

/**
 * Newsletter displacement cap, as a fraction of the layer height. Matches the
 * slack the layer's `scale(1.10)` provides, so the photo never uncovers an edge.
 */
const CIRCLE_PARALLAX_MAX_RATIO = 0.05;

/** Max time to wait for Lenis before unlocking the page (ms). */
const READY_TIMEOUT_MS = 2000;

/** Fallback viewport height for environments that report 0 (jsdom). */
const FALLBACK_VIEWPORT_HEIGHT = 800;

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Owns the homepage's scroll behaviour: Lenis smooth scroll, the two parallax
 * layers, and the IntersectionObserver behind the text reveals.
 *
 * `ready` gates the scroll lock and the fade-in, and always flips — resolve,
 * reject or timeout. Parallax publishes CSS variables, so no frame hits layout.
 */
export function useScrollEffects() {
  const heroStageRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const parallaxRafRef = useRef<number | null>(null);
  const lenisRef = useRef<InstanceType<typeof import("lenis").default> | null>(null);
  const scrollLockedRef = useRef(false);
  const prefersReducedMotionRef = useRef(false);
  const scrollYRef = useRef(0);

  const [ready, setReady] = useState(false);

  const setScrollLocked = useCallback((locked: boolean) => {
    scrollLockedRef.current = locked;
    const lenis = lenisRef.current;
    if (locked) lenis?.stop();
    else lenis?.start();
  }, []);

  const updateParallax = useCallback(() => {
    const scrollY = scrollYRef.current;

    const heroStage = heroStageRef.current;
    if (heroStage) {
      const hero = heroStage.parentElement;
      const heroHeight = hero?.offsetHeight || window.innerHeight || FALLBACK_VIEWPORT_HEIGHT;
      // Clamped to the hero's own height: past it the layer is off-screen and
      // any further displacement would only inflate the transform.
      const travelled = Math.min(Math.max(scrollY, 0), heroHeight);
      heroStage.style.setProperty("--heroPY", `${(travelled * HERO_PARALLAX_FACTOR).toFixed(1)}px`);
    }

    // The duo's portal stages (`[data-banner-stage]`): the reference's `updateBanners`, measured
    // from each frame's centre and clamped to one frame height either way.
    const bannerViewportHeight = window.innerHeight || FALLBACK_VIEWPORT_HEIGHT;
    rootRef.current?.querySelectorAll<HTMLElement>("[data-banner-stage]").forEach((stage) => {
      const frame = stage.parentElement;
      if (!frame) return;
      const frameHeight = frame.offsetHeight || 600;
      const distance = frame.getBoundingClientRect().top + frameHeight / 2 - bannerViewportHeight / 2;
      const clamped = Math.min(Math.max(-distance, -frameHeight), frameHeight);
      stage.style.setProperty("--bannerPY", `${(clamped * BANNER_PARALLAX_FACTOR).toFixed(1)}px`);
    });

    const circle = circleRef.current;
    const circleHost = circle?.parentElement;
    if (circle && circleHost) {
      // Measured from the section's centre rather than the page top, so the
      // photo sits neutral when the band is centred in the viewport.
      const hostTop = circleHost.getBoundingClientRect().top;
      const circleHeight = circle.offsetHeight || 1;
      const viewportHeight = window.innerHeight || FALLBACK_VIEWPORT_HEIGHT;
      const offset = (viewportHeight / 2 - (hostTop + circleHeight / 2)) * CIRCLE_PARALLAX_FACTOR;
      const maxOffset = circleHeight * CIRCLE_PARALLAX_MAX_RATIO;
      const clamped = Math.max(-maxOffset, Math.min(maxOffset, offset));
      circle.style.setProperty("--circlePY", `${clamped.toFixed(1)}px`);
    }
  }, []);

  // Coalesces scroll and resize bursts into a single write per frame.
  const queueParallax = useCallback(() => {
    if (parallaxRafRef.current !== null) return;
    parallaxRafRef.current = requestAnimationFrame(() => {
      parallaxRafRef.current = null;
      if (!prefersReducedMotionRef.current) updateParallax();
    });
  }, [updateParallax]);

  /**
   * Scrolls to a homepage section the way the reference's `navScroll` does: through Lenis when it
   * runs, a plain jump under reduced motion, the native smooth scroll otherwise.
   */
  const scrollToSection = useCallback((target: HTMLElement) => {
    const top = window.scrollY + target.getBoundingClientRect().top - SECTION_SCROLL_OFFSET_PX;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      window.scrollTo(0, top);
      return;
    }

    const lenis = lenisRef.current;
    if (lenis) {
      lenis.scrollTo(target, {
        offset: -SECTION_SCROLL_OFFSET_PX,
        duration: SECTION_SCROLL_DURATION_S,
        easing: easeOutCubic,
      });
      return;
    }

    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  // Text reveals are the vocabulary shared with the panel: the trigger lives in
  // `@/lib/motion/use-reveals` and the curves in `globals.css`.
  useReveals(rootRef, EDITORIAL_REVEAL);

  useEffect(() => {
    prefersReducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    document.documentElement.style.overflow = "hidden";

    const safetyTimer = setTimeout(() => {
      document.documentElement.style.overflow = "";
      setReady(true);
    }, READY_TIMEOUT_MS);

    const unlock = () => {
      clearTimeout(safetyTimer);
      document.documentElement.style.overflow = "";
      setReady(true);
    };

    // ─── Lenis smooth scroll ───
    let mounted = true;
    /** Whether this mount took a reference to the shared engine. */
    let heldLenis = false;

    if (prefersReducedMotionRef.current) {
      unlock();
    } else {
      /*
       * One engine for the whole product (`lib/motion/lenis-engine.ts`), not one
       * per page. This hook used to build its own, with its own frame loop, and
       * the PDP's — which never destroyed itself — was often already running:
       * two instances writing a scroll position to the same document every
       * frame, which is why the home's scroll stalled depending on where the
       * visitor came from.
       *
       * `syncTouch` travels in the options because the reference drives touch
       * through Lenis here, and only here.
       */
      heldLenis = true;
      void acquireLenis(LENIS_OPTIONS)
        .then((lenis) => {
          if (!mounted) return;
          if (lenis === null) {
            // Degrades to native scroll; the listener below is already the
            // source of truth in that case.
            unlock();
            return;
          }

          lenisRef.current = lenis;
          if (scrollLockedRef.current) lenis.stop();

          lenis.on("scroll", (event: { scroll: number }) => {
            scrollYRef.current = event.scroll;
            queueParallax();
          });

          unlock();
        })
        .catch(() => {
          // Tech Spec: Lenis loads fault-tolerantly, degrading to native scroll.
          // Unlocking here spares the page the full READY_TIMEOUT_MS wait.
          if (mounted) unlock();
        });
    }

    // Native scroll listener: the source of truth before Lenis attaches, and the
    // only one when it never does.
    const onScroll = () => {
      scrollYRef.current = window.scrollY || 0;
      queueParallax();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", queueParallax);
    onScroll();

    return () => {
      mounted = false;
      document.documentElement.style.overflow = "";
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", queueParallax);
      if (parallaxRafRef.current !== null) cancelAnimationFrame(parallaxRafRef.current);
      // The engine is shared and reference-counted: hand the reference back and
      // let the last holder destroy it. Destroying it here would take the scroll
      // away from a surface that is still using it.
      lenisRef.current = null;
      if (heldLenis) {
        heldLenis = false;
        releaseLenis();
      }
      clearTimeout(safetyTimer);
    };
  }, [queueParallax]);

  return { circleRef, heroStageRef, ready, rootRef, scrollToSection, setScrollLocked };
}
